import { externalRoutes, routes } from '~constants/routes';
import { useTranslations } from '~i18n/hooks';
import CharacterIcon from '~public/assets/icons/navigation/character.svg';
import ChatsIcon from '~public/assets/icons/navigation/chats.svg';
import ExploreIcon from '~public/assets/icons/navigation/explore.svg';
import GalleryIcon from '~public/assets/icons/navigation/gallery.svg';
import VideosIcon from '~public/assets/icons/navigation/videos.svg';
import { isSafeApp } from '~shared/utils/host';

import type { NavigationElement, NavigationPathWithoutText } from './types';

const explore: NavigationPathWithoutText = {
	path: isSafeApp ? routes.explore.home : routes.explore.models,
	additionalActivePaths: [routes.explore.home],
	icon: <ExploreIcon />,
	isDemo: true,
	mobileOrder: 0,
	'data-name': 'explore-menu-item',
};

const chat: NavigationPathWithoutText = {
	path: routes.chats.home,
	icon: <ChatsIcon />,
	isDemo: true,
	mobileOrder: 2,
	withMessageCounter: true,
	'data-name': 'chat-menu-item',
};

const gallery: NavigationPathWithoutText = {
	path: routes.gallery.home,
	isDemo: true,
	icon: <GalleryIcon />,
	'data-name': 'gallery-menu-item',
	disabled: isSafeApp,
};

const createCharacter: NavigationPathWithoutText = {
	path: routes.generate.character,
	icon: <CharacterIcon />,
	isDemo: true,
	'data-name': 'create-character-menu-item',
	disabled: isSafeApp,
};

const generateImagesCommunity = {
	path: routes.generate.images.community,
	additionalActivePaths: [
		routes.generate.images.community,
		routes.generate.images.my,
		routes.generate.images.liked,
		routes.generate.images.unlocked,
	],
	icon: <GalleryIcon />,
	isDemo: true,
	'data-name': 'generate-images-menu-item',
	disabled: isSafeApp,
} satisfies NavigationPathWithoutText;

const generateImagesMy = {
	...generateImagesCommunity,
	path: routes.generate.images.my,
} satisfies NavigationPathWithoutText;

const generateVideosCommunity = {
	path: routes.generate.videos.community,
	additionalActivePaths: [
		routes.generate.videos.community,
		routes.generate.videos.my,
		routes.generate.videos.liked,
		routes.generate.videos.unlocked,
	],
	icon: <VideosIcon />,
	isDemo: false,
	new: !isSafeApp,
	'data-name': 'generate-videos-menu-item',
	disabled: isSafeApp,
} satisfies NavigationPathWithoutText;

const generateVideosMy = {
	...generateVideosCommunity,
	path: routes.generate.videos.my,
} satisfies NavigationPathWithoutText;

// const academyUrl = getSubdomainUrlStr('academy');

const becomeAffiliate: NavigationPathWithoutText = {
	path: externalRoutes.becomeAffiliate,
	isDemo: true,
	isNativeLink: true,
	'data-name': 'become-affiliate-menu-item',
};
const creatorPartnership: NavigationPathWithoutText = {
	path: externalRoutes.creatorUrl,
	isDemo: true,
	isNativeLink: true,
	'data-name': 'create-partnership-menu-item',
};

const aboutUs: NavigationPathWithoutText = {
	path: routes.company.about,
	targetBlank: true,
	isDemo: true,
	isNativeLink: true,
	'data-name': 'about-us-menu-item',
};

const advisory: NavigationPathWithoutText = {
	path: routes.company.advisory,
	targetBlank: true,
	isDemo: true,
	isNativeLink: true,
	'data-name': 'advisory-menu-item',
};

export function useNavigationPaths(
	demo: boolean,
	isLoading: boolean
): NavigationElement[] {
	const t = useTranslations('Header');

	const links = [
		{
			...explore,
			text: t('exploreMenuTitle'),
		},
	];

	if (!isSafeApp) {
		links.push({
			...(isLoading || demo ? generateImagesMy : generateImagesCommunity),
			additionalActivePaths: [
				...generateImagesCommunity.additionalActivePaths,
				...generateVideosCommunity.additionalActivePaths,
			],
			text: t('galleryMenuTitle'),
		});
		links.push({
			...createCharacter,
			text: t('createV2MenuTitle'),
		});
	}

	links.push({
		...chat,
		text: t('chatsMenuTitle'),
	});

	return links;
}

export function useNavigationPathsSideMenu(
	demo: boolean,
	isLoading: boolean
): NavigationElement[] {
	const t = useTranslations('SideMenu');

	return [
		{
			text: '',
			children: [
				{
					...explore,
					text: t('exploreMenuTitle'),
				},
				...(!isLoading && !demo
					? [
							{
								...gallery,
								text: t('galleryMenuTitle'),
							},
					  ]
					: []),
				{
					...chat,
					text: t('chatsMenuTitle'),
				},
			],
			'data-name': 'main-section',
		},
		...(!isSafeApp
			? [
					{
						text: t('aiToolsMenuSectionTitle'),
						children: [
							{
								...createCharacter,
								text: t('createCharacterMenuTitle'),
							},
							{
								...(isLoading || demo
									? generateImagesMy
									: generateImagesCommunity),
								text: t('generateImagesMenuTitle'),
							},
							{
								...(isLoading || demo
									? generateVideosMy
									: generateVideosCommunity),
								text: t('generateVideosMenuTitle'),
							},
						],
						'data-name': 'ai-tools-section',
					},
			  ]
			: []),
		{
			text: t('earnMenuSectionTitle'),
			children: [
				{ ...becomeAffiliate, text: t('affiliateMenuTitle') },
				...(!isSafeApp
					? [{ ...creatorPartnership, text: t('partnershipMenuTitle') }]
					: []),
			],
			'data-name': 'earn-section',
		},
		{
			text: '',
			children: [
				{ ...aboutUs, text: t('aboutUsMenuTitle') },
				{ ...advisory, text: t('advisoryMenuTitle') },
			],
			'data-name': 'about-section',
		},
	];
}
